import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { EditorContent, useEditor, useEditorState, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import {
  Bold,
  Code,
  Heading2,
  Heading3,
  Italic,
  Link2,
  Link2Off,
  List,
  ListOrdered,
  Quote,
  Strikethrough,
  Underline as UnderlineIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { plainTextToHtml, sanitizeRichText } from '@/lib/rich-text'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import type { AvatarPerson } from '@/components/composites/user-avatar'
import { MentionList } from '@/components/composites/mention-list'
import {
  mentionKeyAction,
  useEscapeCloses,
  useMentionHighlight,
  useMentionIds,
} from '@/components/composites/mention-keyboard'

/**
 * Rich text, read and written — and the ONLY module in the app that may call
 * `dangerouslySetInnerHTML`.
 *
 * That concentration is the point. Rendering stored markup is a security boundary, and a
 * boundary with five doors is not a boundary. Everything that shows a description goes
 * through `RichText`; everything that edits one goes through `RichTextEditor`; the injection
 * happens in one place, four lines below one `sanitizeRichText` call.
 *
 * The `format` prop is not a hint, it is the instruction. A row written before rich text
 * existed holds PLAIN TEXT, and a description that happens to read `<3 deploys/week` must
 * appear on screen exactly as typed rather than as a broken tag. `'text'` therefore never
 * touches `dangerouslySetInnerHTML` at all — it renders as a text node with
 * `whitespace-pre-wrap`, which is precisely what the app did before this component existed.
 */

export interface RichTextProps {
  /** The stored value. Plain text or sanitized HTML, per `format`. */
  value: string
  /** `cards.descriptionFormat`. Absent is treated as `'text'`, the safe reading. */
  format: 'text' | 'html' | undefined
  className?: string
  /** Shown when the value is blank, so an empty field reads as empty rather than missing. */
  placeholder?: string
}

export function RichText({ value, format, className, placeholder }: RichTextProps) {
  if (!value.trim()) {
    return placeholder ? (
      <p className={cn('text-sm text-text-muted', className)}>{placeholder}</p>
    ) : null
  }

  // Plain text stays plain text. No parsing, no injection, line breaks preserved.
  if (format !== 'html') {
    return <p className={cn('text-sm whitespace-pre-wrap', className)}>{value}</p>
  }

  return (
    <div
      className={cn('rich-text text-sm', className)}
      // The server sanitizes on write and this sanitizes on read. Belt and braces: a string
      // from the network is never injected on the server's word alone.
      dangerouslySetInnerHTML={{ __html: sanitizeRichText(value) }}
    />
  )
}

export interface RichTextEditorProps {
  /** Seed value. The editor is uncontrolled after mount — remount with `key` to reseed. */
  value: string
  /** How to READ `value`. A `'text'` row is escaped into paragraphs, never parsed. */
  format?: 'text' | 'html'
  /** Called with the editor's HTML on every change. */
  onChange: (html: string) => void
  onBlur?: () => void
  disabled?: boolean
  placeholder?: string
  /** Draws the invalid ring, matching `aria-invalid` on the plain inputs. */
  invalid?: boolean
  ariaLabel?: string
  className?: string
  /** Optional people picker for `@Name` completion inside rich text fields. */
  mentionPeople?: readonly AvatarPerson[]
}

/**
 * The editor is configured to emit only the tags the backend's allow-list keeps.
 *
 * Anything it could produce that the sanitizer would drop is disabled at the source —
 * `horizontalRule` (`<hr>`) and `h1`/`h4`–`h6` in particular. A button the user can press
 * that produces markup silently deleted on save is worse than no button.
 */
export function RichTextEditor({
  value,
  format = 'html',
  onChange,
  onBlur,
  disabled = false,
  placeholder,
  invalid = false,
  ariaLabel,
  className,
  mentionPeople = [],
}: RichTextEditorProps) {
  const [mention, setMention] = useState<{ from: number; to: number; query: string } | null>(null)
  const { listId, optionId } = useMentionIds()
  const contentRef = useRef<HTMLDivElement>(null)
  /**
   * The picker's key handling, refreshed every render. It is read through a ref because
   * ProseMirror captures `editorProps` once, at creation — and it has to run as ProseMirror's
   * `handleKeyDown` rather than a React `onKeyDown`, since by the time React's delegated
   * handler sees the event ProseMirror has already split the paragraph or moved the caret.
   */
  const mentionKeyRef = useRef<(event: KeyboardEvent) => boolean>(() => false)
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Only h2 and h3 are on the allow-list; the page already owns h1.
        heading: { levels: [2, 3] },
        // `<hr>` is not allowed and would be stripped on save.
        horizontalRule: false,
        // Registered explicitly below so the configuration lives in one visible place.
        link: false,
        underline: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        // Same three schemes the backend accepts. `javascript:` is not one of them.
        protocols: ['http', 'https', 'mailto'],
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
    ],
    // Sanitized on the way IN as well: the editor's document is DOM too.
    content: format === 'html' ? sanitizeRichText(value) : plainTextToHtml(value),
    editable: !disabled,
    editorProps: {
      attributes: {
        class: 'rich-text min-h-24 px-3 py-2 text-sm outline-none',
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
      },
      handleKeyDown: (_view, event) => mentionKeyRef.current(event),
    },
    onUpdate: ({ editor: next }) => {
      onChange(next.getHTML())
      syncMention(next)
    },
    onSelectionUpdate: ({ editor: next }) => syncMention(next),
    onBlur: () => {
      setMention(null)
      onBlur?.()
    },
  })

  function syncMention(next: Editor) {
    if (!mentionPeople.length || !next || !next.isEditable) {
      setMention(null)
      return
    }
    const { $from } = next.state.selection
    if (!$from.parent.isTextblock) {
      setMention(null)
      return
    }
    const before = $from.parent.textBetween(0, $from.parentOffset, '\n', '\n')
    const match = before.match(/(?:^|\s)@([^@\n\r]{0,40})$/)
    if (!match) {
      setMention(null)
      return
    }
    const query = match[1] ?? ''
    if (/\s\s/.test(query)) {
      setMention(null)
      return
    }
    const to = $from.pos
    const from = to - query.length - 1
    setMention({ from, to, query })
  }

  // `editable` is not reactive through the options object, and re-creating the editor on a
  // permission change would throw away whatever the user had typed.
  useEffect(() => {
    editor.setEditable(!disabled)
  }, [editor, disabled])

  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      empty: e.isEmpty,
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      underline: e.isActive('underline'),
      strike: e.isActive('strike'),
      h2: e.isActive('heading', { level: 2 }),
      h3: e.isActive('heading', { level: 3 }),
      bulletList: e.isActive('bulletList'),
      orderedList: e.isActive('orderedList'),
      blockquote: e.isActive('blockquote'),
      code: e.isActive('code'),
      link: e.isActive('link'),
      href: (e.getAttributes('link').href as string | undefined) ?? '',
    }),
  })

  const mentionMatches = useMemo(() => {
    if (!mention) return []
    const q = mention.query.trim().toLowerCase()
    return mentionPeople
      .filter((person) => !q || person.displayName.toLowerCase().includes(q))
      .slice(0, 6)
  }, [mention, mentionPeople])

  const chooseMention = (person: AvatarPerson) => {
    if (!mention) return
    editor
      .chain()
      .focus()
      .deleteRange({ from: mention.from, to: mention.to })
      .insertContent(`@${person.displayName} `)
      .run()
    setMention(null)
  }

  const mentionOpen = mention !== null && mentionMatches.length > 0
  const {
    highlighted: mentionIndex,
    setHighlighted: setMentionIndex,
    move: moveMention,
  } = useMentionHighlight(mentionMatches.length, mention ? `${mention.from}:${mention.query}` : '')

  useEscapeCloses(mentionOpen, contentRef, () => setMention(null))

  useLayoutEffect(() => {
    mentionKeyRef.current = (event) => {
      if (!mentionOpen) return false
      const action = mentionKeyAction(event)
      if (!action) return false
      event.preventDefault()
      if (action === 'next') moveMention(1)
      else if (action === 'prev') moveMention(-1)
      else if (action === 'close') setMention(null)
      else {
        const person = mentionMatches[mentionIndex]
        if (person) chooseMention(person)
      }
      return true
    }
  })

  // The contenteditable is ProseMirror's node, not ours, so its combobox state is set directly.
  useEffect(() => {
    const dom = contentRef.current?.querySelector<HTMLElement>('[contenteditable]')
    if (!dom || !mentionPeople.length) return
    dom.setAttribute('role', 'combobox')
    dom.setAttribute('aria-autocomplete', 'list')
    dom.setAttribute('aria-haspopup', 'listbox')
    dom.setAttribute('aria-expanded', String(mentionOpen))
    if (mentionOpen) {
      dom.setAttribute('aria-controls', listId)
      dom.setAttribute('aria-activedescendant', optionId(mentionIndex))
    } else {
      dom.removeAttribute('aria-controls')
      dom.removeAttribute('aria-activedescendant')
    }
  }, [mentionOpen, mentionIndex, listId, optionId, mentionPeople.length, editor])

  return (
    <div
      className={cn(
        'relative overflow-visible rounded-md border bg-card transition-colors dur-fast',
        'focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50',
        invalid ? 'border-danger' : 'border-input',
        disabled && 'pointer-events-none opacity-60',
        className,
      )}
    >
      {!disabled ? (
        <div className="flex flex-wrap items-center gap-0.5 border-b border-border-soft bg-surface-sunken px-1.5 py-1">
          <ToolButton
            label="Bold"
            active={state.bold}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <Bold className="size-3.5" />
          </ToolButton>
          <ToolButton
            label="Italic"
            active={state.italic}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <Italic className="size-3.5" />
          </ToolButton>
          <ToolButton
            label="Underline"
            active={state.underline}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <UnderlineIcon className="size-3.5" />
          </ToolButton>
          <ToolButton
            label="Strikethrough"
            active={state.strike}
            onClick={() => editor.chain().focus().toggleStrike().run()}
          >
            <Strikethrough className="size-3.5" />
          </ToolButton>

          <ToolDivider />

          <ToolButton
            label="Heading"
            active={state.h2}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            <Heading2 className="size-3.5" />
          </ToolButton>
          <ToolButton
            label="Sub-heading"
            active={state.h3}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          >
            <Heading3 className="size-3.5" />
          </ToolButton>

          <ToolDivider />

          <ToolButton
            label="Bulleted list"
            active={state.bulletList}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <List className="size-3.5" />
          </ToolButton>
          <ToolButton
            label="Numbered list"
            active={state.orderedList}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <ListOrdered className="size-3.5" />
          </ToolButton>
          <ToolButton
            label="Quote"
            active={state.blockquote}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          >
            <Quote className="size-3.5" />
          </ToolButton>
          <ToolButton
            label="Code"
            active={state.code}
            onClick={() => editor.chain().focus().toggleCode().run()}
          >
            <Code className="size-3.5" />
          </ToolButton>

          <ToolDivider />

          <LinkButton
            active={state.link}
            href={state.href}
            onApply={(href) =>
              editor
                .chain()
                .focus()
                .extendMarkRange('link')
                .setLink({ href })
                .run()
            }
            onRemove={() => editor.chain().focus().extendMarkRange('link').unsetLink().run()}
          />
        </div>
      ) : null}

      <div ref={contentRef} className="relative">
        {/* The placeholder is rendered here rather than pulled in as another extension —
            it is one absolutely-positioned line, and `isEmpty` already tells us when. */}
        {placeholder && state.empty ? (
          <p className="pointer-events-none absolute top-2 left-3 text-sm text-text-muted">
            {placeholder}
          </p>
        ) : null}
        <EditorContent editor={editor} />
        {mentionOpen ? (
          <MentionList
            id={listId}
            optionId={optionId}
            people={mentionMatches}
            highlighted={mentionIndex}
            onHighlight={setMentionIndex}
            onChoose={chooseMention}
            className="top-full left-2 mt-1 w-72"
          />
        ) : null}
      </div>
    </div>
  )
}

function ToolDivider() {
  return <span aria-hidden className="mx-0.5 h-4 w-px bg-border" />
}

function ToolButton({
  label,
  active,
  onClick,
  children,
}: {
  label: string
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      title={label}
      // `onMouseDown` prevented: clicking a toolbar button must not blur the editor, or the
      // selection the command is about to act on is gone by the time it runs.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn(
        'grid size-7 place-items-center rounded-sm transition-colors dur-fast',
        active
          ? 'bg-brand-soft text-brand-dark'
          : 'text-text-secondary hover:bg-accent hover:text-accent-foreground',
      )}
    >
      {children}
    </button>
  )
}

function LinkButton({
  active,
  href,
  onApply,
  onRemove,
}: {
  active: boolean
  href: string
  onApply: (href: string) => void
  onRemove: () => void
}) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('')

  return (
    <>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (next) setDraft(href)
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Link"
            aria-pressed={active}
            title="Link"
            onMouseDown={(e) => e.preventDefault()}
            className={cn(
              'grid size-7 place-items-center rounded-sm transition-colors dur-fast',
              active
                ? 'bg-brand-soft text-brand-dark'
                : 'text-text-secondary hover:bg-accent hover:text-accent-foreground',
            )}
          >
            <Link2 className="size-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-2">
          <form
            className="flex items-center gap-1.5"
            onSubmit={(e) => {
              e.preventDefault()
              const url = draft.trim()
              if (url) onApply(url)
              setOpen(false)
            }}
          >
            <Input
              autoFocus
              type="url"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="https://…"
              className="h-8 text-xs"
            />
            <Button type="submit" size="sm" className="h-8">
              Apply
            </Button>
          </form>
          <p className="mt-1.5 px-0.5 text-tiny text-text-muted">
            Only http, https and mailto links are kept.
          </p>
        </PopoverContent>
      </Popover>

      {active ? (
        <ToolButton
          label="Remove link"
          active={false}
          onClick={onRemove}
        >
          <Link2Off className="size-3.5" />
        </ToolButton>
      ) : null}
    </>
  )
}
